import { z } from 'zod';

/**
 * Tournament.policies — anti-cheat & entry policies.
 * editWindowMinutes: a caddy may change an already-saved hole score only within N minutes of its first
 * entry (default 30) and only while the round is IN_PROGRESS; later changes need an admin correction
 * with a reason.
 */
export const TournamentPoliciesSchema = z.object({
  editWindowMinutes: z
    .int()
    .min(0)
    .max(24 * 60)
    .default(30),
  /** caddy may edit any hole of the assigned group (true) or only the last N holes entered (false) */
  caddyCanEditPastHoles: z.boolean().default(true),
  requirePutts: z.boolean().default(false),
  /** strokes above par + N trigger a warning (confirmation) on the caddy device */
  warnAboveParBy: z.int().min(1).max(10).default(4),
  /** absolute maximum strokes per hole accepted by the server */
  maxStrokesPerHole: z.int().min(5).max(30).default(15),
  maxPuttsPerHole: z.int().min(1).max(10).default(6),
  /** club events: participants may enter their own group's scores (셀프 입력) */
  allowSelfEntry: z.boolean().default(false),
  /**
   * caddy login needs the PIN (default). false = caddy code + the bib of the player the caddy
   * carries (1 caddy per player, Vietnamese courses — §15 #472)
   */
  caddyPin: z.boolean().default(false),
  /**
   * caddy-device sign-off asks for the player's 4-digit signOffCode (player card / portal).
   * Default off until the course decides how players get the code (§15 #484): the player checks
   * the card on the caddy's phone and confirms.
   */
  signOffCode: z.boolean().default(false),
  /** players must sign the scorecard before the round can be locked */
  requireSignOffBeforeLock: z.boolean().default(false),
  /** auto-lock the round N minutes after all groups finished (null = manual lock only) */
  autoLockMinutes: z.int().min(0).nullable().default(null),
  /** leaderboard visibility for spectators */
  publicLeaderboard: z.boolean().default(true),
  /** hide the last N holes from the public leaderboard (suspense; 0 = off) */
  hideLastHoles: z.int().min(0).max(9).default(0),
  /** contest distances accepted range (m) */
  nearPinMaxMeters: z.number().positive().default(30),
  longestMinMeters: z.number().positive().default(100),
  longestMaxMeters: z.number().positive().default(450),
});
export type TournamentPolicies = z.infer<typeof TournamentPoliciesSchema>;

export const DEFAULT_POLICIES: TournamentPolicies = TournamentPoliciesSchema.parse({});
